import { useCallback } from 'react';

import { useDeleteOperation } from 'api/requests/operation/useDeleteOperation';
import { OperationEntity } from 'api/types/entity/operation';
import { useServerErrorNotify } from 'hooks';
import { ROUTES, useNavigate } from 'libs/navigation';
import { useDialog } from 'libs/ui/Dialog/useDialog';

import { useLockAndUnlockOperation } from './useLockAndUnlockOperation';

export const useDeleteAction = (operation: OperationEntity | null) => {
  const { canGoBack, navigate, goBack } = useNavigate();

  const { lockOperationIfPossible, unlockOperation } =
    useLockAndUnlockOperation(operation);

  const {
    deleteOperation,
    error,
    loading: loadingDelete,
  } = useDeleteOperation();

  const {
    isOpen: isOpenDialogConfirmDelete,
    onClose: onCloseDialogConfirmDelete,
    open: openDialogConfirmDelete,
  } = useDialog();

  const handleCloseDialogConfirmDelete = useCallback(() => {
    unlockOperation?.();
    onCloseDialogConfirmDelete();
  }, [unlockOperation, onCloseDialogConfirmDelete]);

  const onOpenDialogConfirmDelete = useCallback(async () => {
    const isPossibleEdit = await lockOperationIfPossible?.();
    if (isPossibleEdit) {
      openDialogConfirmDelete();
    }
  }, [lockOperationIfPossible, openDialogConfirmDelete]);

  const onDelete = useCallback(async () => {
    if (await deleteOperation(operation!.id, operation?.versionNumber!)) {
      if (canGoBack) {
        goBack();
      } else {
        navigate(ROUTES.operations.fullPath, {});
      }
    }
  }, [canGoBack, deleteOperation, goBack, navigate, operation]);

  useServerErrorNotify(error);

  return {
    loadingDelete,
    isOpenDialogConfirmDelete,
    onCloseDialogConfirmDelete: handleCloseDialogConfirmDelete,
    onOpenDialogConfirmDelete,
    onDelete,
  };
};
